/**
 * Geometry Utilities for Rotation-Aware Operations
 *
 * Provides coordinate transformation functions for working with rotated nodes.
 * All angles are in radians.
 */

import type { Point } from './types';

/**
 * Rotate a point around the origin by the given angle.
 *
 * @param point - Point to rotate
 * @param angle - Rotation angle in radians (positive = clockwise)
 * @returns Rotated point
 */
export function rotatePoint(point: Point, angle: number): Point {
	const cos = Math.cos(angle);
	const sin = Math.sin(angle);
	return {
		x: point.x * cos - point.y * sin,
		y: point.x * sin + point.y * cos,
	};
}

/**
 * Transform a world-space point to node-local coordinate space.
 *
 * Local space is axis-aligned with the node's orientation:
 * - Origin at node center
 * - X axis points along node's "right" direction
 * - Y axis points along node's "down" direction
 *
 * @param point - Point in world coordinates
 * @param center - Node center in world coordinates
 * @param rotation - Node rotation in radians
 * @returns Point in local coordinates
 */
export function worldToLocal(point: Point, center: Point, rotation: number): Point {
	// Translate to center, then rotate by -rotation to align with local axes
	const dx = point.x - center.x;
	const dy = point.y - center.y;
	const cos = Math.cos(-rotation);
	const sin = Math.sin(-rotation);
	return {
		x: dx * cos - dy * sin,
		y: dx * sin + dy * cos,
	};
}

/**
 * Transform a node-local point to world coordinate space.
 *
 * Inverse of worldToLocal.
 *
 * @param local - Point in local coordinates
 * @param center - Node center in world coordinates
 * @param rotation - Node rotation in radians
 * @returns Point in world coordinates
 */
export function localToWorld(local: Point, center: Point, rotation: number): Point {
	// Rotate by +rotation, then translate to center
	const cos = Math.cos(rotation);
	const sin = Math.sin(rotation);
	return {
		x: center.x + local.x * cos - local.y * sin,
		y: center.y + local.x * sin + local.y * cos,
	};
}

/**
 * Get the local-space anchor point for a resize handle (the opposite corner/edge).
 *
 * For a node with half-width hw and half-height hh:
 * - Corner handles: anchor is opposite corner
 * - Edge handles: anchor is center of opposite edge
 *
 * @param halfWidth - Half of node width
 * @param halfHeight - Half of node height
 * @param handle - The resize handle being dragged
 * @returns Anchor point in local coordinates (relative to node center)
 */
export function getLocalAnchor(
	halfWidth: number,
	halfHeight: number,
	handle: string
): Point {
	switch (handle) {
		case 'nw':
			return { x: halfWidth, y: halfHeight }; // Anchor at SE
		case 'n':
			return { x: 0, y: halfHeight }; // Anchor at S (center)
		case 'ne':
			return { x: -halfWidth, y: halfHeight }; // Anchor at SW
		case 'w':
			return { x: halfWidth, y: 0 }; // Anchor at E (center)
		case 'e':
			return { x: -halfWidth, y: 0 }; // Anchor at W (center)
		case 'sw':
			return { x: halfWidth, y: -halfHeight }; // Anchor at NE
		case 's':
			return { x: 0, y: -halfHeight }; // Anchor at N (center)
		case 'se':
			return { x: -halfWidth, y: -halfHeight }; // Anchor at NW
		default:
			return { x: 0, y: 0 };
	}
}
